import ResponsiveImage, { ImageParams } from 'components/Common/ResponsiveImage'
import Image from 'components/Common/Image'
import Clickable from 'components/Common/Clickable'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled from 'styled-components'
import IconButton from '../Button/IconButton'
import TextButton from '../Button/TextButton'
import LineAngleLeftIcon from '../Icons/line/LineAngleLeftIcon'
import LineAngleRightIcon from '../Icons/line/LineAngleRightIcon'
import LineArrowLeftIcon from '../Icons/line/LineArrowLeftIcon'
import LightBox from '../Modal/LightBox'
import BodyText from '../Typography/BodyText'
import BaseImageCarousel, { ImageCarouselRefInterface } from './BaseImageCarousel'
import ImageCarousel from './ImageCarousel'
import ImageGalleryThumbnailStrip from './ImageGalleryThumbnailStrip'
import { EmptyArray, sortBy } from 'lib/array/arrayUtils'
import SolidPlayIcon from '../Icons/solid/SolidPlayIcon'
import VimeoThumbnail from 'components/Common/Video/VimeoThumbnail'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import { getImageAtCarouselIndex } from './imageGalleryUtils'

const GridContainer = styled.div`
  height: 100%;
  width: 100%;
  overflow-y: auto;
  padding: ${rem(60)} ${rem(24)} ${rem(24)};
`

const ImageGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${rem(8)};
  max-width: ${rem(1200)};
  margin: 0 auto;

  ${mediaQueryUp.tablet} {
    grid-template-columns: repeat(3, 1fr);
    gap: ${rem(12)};
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: repeat(4, 1fr);
  }
`

const GridImageButton = styled(Clickable)<{ $isSelected: boolean }>`
  aspect-ratio: 4 / 3;
  border-radius: ${props => props.theme.borderRadius.S};
  overflow: hidden;
  position: relative;
  padding: 0;
  background: none;
  border: none;
  cursor: pointer;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: inherit;
    border: 3px solid ${props => props.$isSelected ? props.theme.palette.highlight.primary.normalBackground : 'transparent'};
    transition: border-color 0.1s ease-in-out;
    pointer-events: none;
    z-index: 1;
  }

  &:hover::after {
    border-color: ${props => props.theme.palette.highlight.primary.normalBackground};
  }

  &:focus-visible::after {
    border-color: ${props => props.theme.palette.highlight.primary.normalBackground};
    box-shadow: inset 0 0 0 2px ${props => props.theme.palette.neutral.default.eight};
  }

  img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const VideoBadge = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: ${rem(48)};
  height: ${rem(48)};
  border-radius: ${rem(24)};
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  color: ${props => props.theme.palette.neutral.default.eight};
  z-index: 2;

  svg {
    width: ${rem(22)};
    height: ${rem(22)};
  }
`

const NavigationArrow = styled(IconButton)`
  display: none;
  z-index: 1;
  transition: color 0.2s, background-color 0.2s, border-color 0.2s, opacity 0.2s;

  ${mediaQueryUp.tablet} {
    display: inline-flex;
  }
`

const SingleViewContainer = styled.div`
  height: 100%;
  display: grid;
  align-items: center;
  position: relative;

  ${mediaQueryUp.tablet} {
    grid-template-columns: 10% 1fr 10%;

    > ${NavigationArrow} {
      align-self: center;
      justify-self: center;
    }
  }

  > [hidden] {
    opacity: 0;
    pointer-events: none;
  }
`

const GalleryReservedRatio = styled.div`
  overflow: hidden;
  aspect-ratio: 16 / 9;
  margin: 0 auto;
  position: relative;
  max-height: 100%;
  width: 100%;
  max-width: ${rem(1200)};
`

const TopSection = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 ${rem(12)};
`

const BackToGalleryButton = styled(TextButton)`
  position: absolute;
  left: ${rem(16)};
  top: ${rem(16)};
  z-index: 2;
`

const BottomTextSection = styled(BodyText)`
  display: flex;
  align-items: start;
  justify-content: center;
  flex-shrink: 0;
  padding: 0 ${rem(12)};
  min-height: ${rem(20)};
`

const GalleryElement = styled.div`
  padding: ${rem(16)} 0;
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: ${rem(12)};
  overflow: hidden;
  min-width: 0;
`

const MediaBlock = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${rem(12)};
  padding: 0 ${rem(12)};
`

const ThumbnailStripWrapper = styled.div`
  flex-shrink: 0;
  overflow: hidden;
`

const galleryImageProps: ImageParams = { mobileAspectRatio: '16:9', quality: 'good', fit: 'center' }
const gridImageProps: ImageParams = { quality: 'good', fit: 'center' }

type ViewMode = 'grid' | 'single'

type ImageCarouselProps = Pick<React.ComponentProps<typeof ImageCarousel>, 'images' | 'interstitials' | 'onImageChange' | 'defaultIndex'>

interface Props extends React.PropsWithChildren, ImageCarouselProps {
}

function ImageGalleryGridLightbox(props: Props) {
  const { images, children, defaultIndex = 0, onImageChange, interstitials = EmptyArray }: Props = props
  const carouselRef = useRef<ImageCarouselRefInterface>(null)
  const elementContext = useModalElementContext()
  const [viewMode, setViewMode] = useState<ViewMode>('grid')
  const [currentImage, setCurrentImage] = useState<App.Image | undefined>(() => getImageAtCarouselIndex(defaultIndex, images, interstitials))
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(defaultIndex)
  const [selectedGridIndex, setSelectedGridIndex] = useState<number>(defaultIndex)

  const gridItems = useMemo(() => {
    const allItems = [
      ...interstitials.map(interstitial => ({
        id: interstitial.id,
        position: interstitial.position,
        type: 'video' as const,
      })),
      ...images.map((image, index) => ({
        id: image.id ?? image.url ?? index.toString(),
        position: index + 1,
        type: 'image' as const,
        image,
      })),
    ]

    return sortBy(allItems, item => {
      if (item.position === 'end') {
        return 10000
      } else if (item.position === 'start') {
        return -1
      } else {
        return item.position
      }
    }, 'asc')
  }, [images, interstitials])

  const videoMeta = useMemo(() => {
    const firstVideo = interstitials.find(i => i.id.startsWith('video-'))
    const videoId = firstVideo?.id.replace('video-', '')
    const videoPosition = typeof firstVideo?.position === 'number' ? firstVideo.position : undefined
    return {
      videoId,
      videoPosition,
    }
  }, [interstitials])

  useEffect(() => {
    if (viewMode === 'single') {
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'ArrowLeft') {
          carouselRef.current?.previousImage()
        } else if (e.key === 'ArrowRight') {
          carouselRef.current?.nextImage()
        } else if (e.key === 'Escape') {
          setViewMode('grid')
        }
      }

      window.addEventListener('keydown', onKeyDown)
      return () => window.removeEventListener('keydown', onKeyDown)
    }
  }, [viewMode])

  const onArrowClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    e.preventDefault()
    const direction = e.currentTarget.dataset.direction as string
    if (direction === 'left') {
      carouselRef.current?.previousImage()
    } else {
      carouselRef.current?.nextImage()
    }
  }, [])

  const onChangeImage = useCallback((index: number, image?: App.Image) => {
    onImageChange?.(index, image)
    setCurrentImage(image)
    setCurrentImageIndex(index)
    setSelectedGridIndex(index)
  }, [onImageChange])

  const onThumbnailSelect = useCallback((index: number) => {
    carouselRef.current?.goToIndex(index, 'instant')
  }, [])

  const onGridItemClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const index = Number(event.currentTarget.dataset.index ?? '0')
    setSelectedGridIndex(index)
    setCurrentImageIndex(index)
    const item = gridItems[index]
    setCurrentImage(item?.type === 'image' ? item.image : undefined)
    setViewMode('single')
  }, [gridItems])

  const onBackToGrid = useCallback(() => {
    setViewMode('grid')
  }, [])

  const onBackgroundMouseDown = useCallback((event: React.MouseEvent<HTMLElement>) => {
    if (event.target === event.currentTarget) {
      event.stopPropagation()
      elementContext?.resolve()
    }
  }, [elementContext])

  if (viewMode === 'grid') {
    return (
      <LightBox>
        <GridContainer onMouseDown={onBackgroundMouseDown}>
          <ImageGrid onMouseDown={onBackgroundMouseDown}>
            {gridItems.map((item, index) => {
              const isSelected = index === selectedGridIndex
              return (
                <GridImageButton
                  key={item.id}
                  $isSelected={isSelected}
                  onClick={onGridItemClick}
                  data-index={index}
                  aria-label={item.type === 'video' ? 'View video' : `View image ${index + 1}${item.image?.title ? `: ${item.image.title}` : ''}`}
                >
                  {item.type === 'video' && (
                    <>
                      {videoMeta.videoId && (
                        <VimeoThumbnail vimeoVideoId={videoMeta.videoId} alt="Video thumbnail" />
                      )}
                      <VideoBadge aria-hidden="true">
                        <SolidPlayIcon />
                      </VideoBadge>
                    </>
                  )}
                  {item.type === 'image' && item.image?.id && (
                    <ResponsiveImage
                      id={item.image.id}
                      alt={item.image.title ?? `Image ${index + 1}`}
                      {...gridImageProps}
                      loading="lazy"
                    />
                  )}
                  {item.type === 'image' && !item.image?.id && item.image?.url && (
                    <Image
                      image={item.image}
                      alt={item.image.title ?? `Image ${index + 1}`}
                      fit="center"
                      loading="lazy"
                    />
                  )}
                </GridImageButton>
              )
            })}
          </ImageGrid>
        </GridContainer>
      </LightBox>
    )
  }

  return (
    <LightBox>
      <BackToGalleryButton
        kind="tertiary"
        variant="ghost"
        startIcon={<LineArrowLeftIcon />}
        onClick={onBackToGrid}
      >
        Gallery
      </BackToGalleryButton>
      <SingleViewContainer onMouseDown={onBackgroundMouseDown}>
        <NavigationArrow
          onClick={onArrowClick}
          kind="secondary"
          variant="dark"
          shape="circle"
          size="medium"
          data-direction="left"
          hidden={images.length + interstitials.length <= 1}
        >
          <LineAngleLeftIcon />
        </NavigationArrow>
        <GalleryElement onMouseDown={onBackgroundMouseDown}>
          <TopSection>
            <BodyText variant="medium" colour="neutral-eight" align="center">
              {(currentImageIndex + 1)} / {images.length + interstitials.length}
            </BodyText>
          </TopSection>
          <MediaBlock>
            <GalleryReservedRatio>
              <BaseImageCarousel
                defaultIndex={currentImageIndex}
                imageParams={galleryImageProps}
                carouselRef={carouselRef}
                images={images}
                onImageChange={onChangeImage}
                interstitials={interstitials}
              />
              {children}
            </GalleryReservedRatio>
            <BottomTextSection variant="medium" colour="neutral-eight" align="center">
              {currentImage?.title ?? ''}&nbsp;
            </BottomTextSection>
          </MediaBlock>
          <ThumbnailStripWrapper>
            <ImageGalleryThumbnailStrip
              images={images}
              videoIds={videoMeta.videoId ? [videoMeta.videoId] : EmptyArray}
              videoPosition={videoMeta.videoPosition}
              selectedIndex={currentImageIndex}
              onSelect={onThumbnailSelect}
              showScrollArrows
            />
          </ThumbnailStripWrapper>
        </GalleryElement>
        <NavigationArrow
          onClick={onArrowClick}
          variant="dark"
          kind="secondary"
          shape="circle"
          size="medium"
          data-direction="right"
          hidden={images.length + interstitials.length <= 1}
        >
          <LineAngleRightIcon />
        </NavigationArrow>
      </SingleViewContainer>
    </LightBox>
  )
}

export default ImageGalleryGridLightbox
